import { useState, useEffect, useRef } from 'react';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { 
  FileText, 
  Download, 
  Upload, 
  Car, 
  Users, 
  Receipt, 
  TrendingUp,
  FileSpreadsheet,
  Loader2,
  BarChart3,
  PieChart as PieChartIcon,
  Calendar,
  RefreshCw
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Area,
  AreaChart
} from 'recharts';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#D4AF37', '#10B981', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4'];

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount || 0);
};

const formatCompact = (value) => {
  if (value >= 100000) return `₹${(value / 100000).toFixed(1)}L`;
  if (value >= 1000) return `₹${(value / 1000).toFixed(1)}K`;
  return `₹${value}`;
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 rounded-lg shadow-lg border border-slate-200">
        <p className="font-medium text-slate-900">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ color: entry.color }} className="text-sm">
            {entry.name}: {typeof entry.value === 'number' && entry.value > 100 
              ? formatCurrency(entry.value) 
              : entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const Reports = () => {
  const [activeTab, setActiveTab] = useState('analytics');
  const [plReport, setPlReport] = useState(null);
  const [vehicleReport, setVehicleReport] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [dateMode, setDateMode] = useState('month'); // 'month' or 'range'
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importType, setImportType] = useState('');
  const fileInputRef = useRef(null);

  const token = localStorage.getItem('raftaar_token');
  const headers = { Authorization: `Bearer ${token}` };

  // Set default date range (last 6 months)
  useEffect(() => {
    const today = new Date();
    const sixMonthsAgo = new Date(today);
    sixMonthsAgo.setMonth(today.getMonth() - 6);
    setStartDate(sixMonthsAgo.toISOString().split('T')[0]);
    setEndDate(today.toISOString().split('T')[0]);
  }, []);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const params = dateMode === 'range' && startDate && endDate
        ? `start_date=${startDate}&end_date=${endDate}`
        : '';
      const response = await axios.get(`${BACKEND_URL}/api/reports/analytics?${params}`, { headers });
      setAnalytics(response.data);
    } catch (error) {
      toast.error('Failed to fetch analytics');
    } finally {
      setLoading(false);
    }
  };

  const fetchPlReport = async () => {
    setLoading(true);
    try {
      let url = `${BACKEND_URL}/api/reports/monthly-pl`;
      if (dateMode === 'range' && startDate && endDate) {
        url += `?start_date=${startDate}&end_date=${endDate}`;
      } else {
        url += `?month=${selectedMonth}&year=${selectedYear}`;
      }
      const response = await axios.get(url, { headers });
      setPlReport(response.data);
    } catch (error) {
      toast.error('Failed to fetch P&L report');
    } finally {
      setLoading(false);
    }
  };

  const fetchVehicleReport = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/reports/vehicle-profitability`, { headers });
      setVehicleReport(response.data);
    } catch (error) {
      toast.error('Failed to fetch vehicle report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (startDate && endDate) {
      fetchAnalytics();
      fetchPlReport();
      fetchVehicleReport();
    }
  }, []);

  const handleRefresh = () => {
    fetchAnalytics();
    fetchPlReport();
  };

  const downloadPdf = async (type) => {
    try {
      let url = type === 'pl' 
        ? `${BACKEND_URL}/api/reports/monthly-pl/pdf`
        : `${BACKEND_URL}/api/reports/vehicle-profitability/pdf`;
      
      if (type === 'pl') {
        url += dateMode === 'range' && startDate && endDate
          ? `?start_date=${startDate}&end_date=${endDate}`
          : `?month=${selectedMonth}&year=${selectedYear}`;
      }
      
      const response = await axios.get(url, { headers, responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = type === 'pl' ? `raftaar_pl_report.pdf` : 'raftaar_vehicle_profitability.pdf';
      link.click();
      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const downloadExcel = async (entity) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/export/${entity}/excel`, {
        headers,
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `raftaar_${entity}.xlsx`;
      link.click();
      toast.success(`${entity} exported successfully`);
    } catch (error) {
      toast.error(`Failed to export ${entity}`);
    }
  };

  const downloadTemplate = async (entity) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/import/template/${entity}`, {
        headers,
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${entity}_template.csv`;
      link.click();
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleImport = async (entity) => {
    setImportType(entity);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }

    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/import/${importType}`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      toast.success(response.data.message);
      if (response.data.errors?.length > 0) {
        toast.warning(`Some rows had errors: ${response.data.errors.slice(0, 3).join(', ')}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  return (
    <Layout title="Reports & Analytics" subtitle="Visualize fleet performance with charts">
      <div className="space-y-6" data-testid="reports-page">
        <input type="file" ref={fileInputRef} className="hidden" accept=".csv" onChange={handleFileChange} />

        {/* Date Range Filter */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap items-end gap-4">
              <div className="space-y-2">
                <Label>Filter Mode</Label>
                <Select value={dateMode} onValueChange={setDateMode}>
                  <SelectTrigger className="w-32" data-testid="date-mode-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="month">By Month</SelectItem>
                    <SelectItem value="range">Date Range</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {dateMode === 'month' ? (
                <>
                  <div className="space-y-2">
                    <Label>Month</Label>
                    <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[...Array(12)].map((_, i) => (
                          <SelectItem key={i + 1} value={(i + 1).toString()}>
                            {new Date(2024, i).toLocaleString('default', { month: 'short' })}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Year</Label>
                    <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                      <SelectTrigger className="w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[2023, 2024, 2025, 2026].map((year) => (
                          <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label>Start Date</Label>
                    <Input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-40"
                      data-testid="start-date-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>End Date</Label>
                    <Input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="w-40"
                      data-testid="end-date-input"
                    />
                  </div>
                </>
              )}

              <Button onClick={handleRefresh} className="bg-[#D4AF37] hover:bg-[#B5952F] text-black" data-testid="refresh-reports-btn">
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </CardContent>
        </Card>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full max-w-lg grid-cols-4">
            <TabsTrigger value="analytics" data-testid="tab-analytics">
              <BarChart3 className="w-4 h-4 mr-2" />
              Charts
            </TabsTrigger>
            <TabsTrigger value="reports" data-testid="tab-reports">
              <FileText className="w-4 h-4 mr-2" />
              Reports
            </TabsTrigger>
            <TabsTrigger value="export" data-testid="tab-export">
              <Download className="w-4 h-4 mr-2" />
              Export
            </TabsTrigger>
            <TabsTrigger value="import" data-testid="tab-import">
              <Upload className="w-4 h-4 mr-2" />
              Import
            </TabsTrigger>
          </TabsList>

          {/* ANALYTICS/CHARTS TAB */}
          <TabsContent value="analytics" className="mt-6 space-y-6">
            {loading && !analytics ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : analytics ? (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="p-4">
                      <p className="text-sm text-slate-500">Total Revenue</p>
                      <p className="text-2xl font-bold text-emerald-600 tabular-nums">
                        {formatCurrency(analytics.summary?.total_revenue)}
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <p className="text-sm text-slate-500">Total OPEX</p>
                      <p className="text-2xl font-bold text-orange-600 tabular-nums">
                        {formatCurrency(analytics.summary?.total_opex)}
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <p className="text-sm text-slate-500">Net Profit</p>
                      <p className={`text-2xl font-bold tabular-nums ${analytics.summary?.net_profit >= 0 ? 'text-[#D4AF37]' : 'text-red-600'}`}>
                        {formatCurrency(analytics.summary?.net_profit)}
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <p className="text-sm text-slate-500">Total Trips</p>
                      <p className="text-2xl font-bold text-blue-600 tabular-nums">
                        {analytics.summary?.total_trips || 0}
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Charts Row 1 */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Monthly Trend Line Chart */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-[#D4AF37]" />
                        Revenue & Profit Trend
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {analytics.charts?.monthly_trend?.length > 0 ? (
                        <ResponsiveContainer width="100%" height={280}>
                          <AreaChart data={analytics.charts.monthly_trend}>
                            <defs>
                              <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#10B981" stopOpacity={0.3}/>
                                <stop offset="95%" stopColor="#10B981" stopOpacity={0}/>
                              </linearGradient>
                              <linearGradient id="colorProfit" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#D4AF37" stopOpacity={0.3}/>
                                <stop offset="95%" stopColor="#D4AF37" stopOpacity={0}/>
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                            <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="#94A3B8" />
                            <YAxis tickFormatter={formatCompact} tick={{ fontSize: 11 }} stroke="#94A3B8" />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend />
                            <Area type="monotone" dataKey="revenue" stroke="#10B981" fill="url(#colorRevenue)" name="Revenue" />
                            <Area type="monotone" dataKey="profit" stroke="#D4AF37" fill="url(#colorProfit)" name="Profit" />
                          </AreaChart>
                        </ResponsiveContainer>
                      ) : (
                        <p className="text-center text-slate-400 py-12">No trend data available</p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Expense Breakdown Pie Chart */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <PieChartIcon className="w-4 h-4 text-[#D4AF37]" />
                        Expense Breakdown
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {analytics.charts?.expense_pie?.length > 0 ? (
                        <ResponsiveContainer width="100%" height={280}>
                          <PieChart>
                            <Pie
                              data={analytics.charts.expense_pie}
                              cx="50%"
                              cy="50%"
                              innerRadius={60}
                              outerRadius={100}
                              paddingAngle={2}
                              dataKey="value"
                              label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                              labelLine={{ stroke: '#94A3B8' }}
                            >
                              {analytics.charts.expense_pie.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(value) => formatCurrency(value)} />
                          </PieChart>
                        </ResponsiveContainer>
                      ) : (
                        <p className="text-center text-slate-400 py-12">No expense data available</p>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Charts Row 2 */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Vehicle Performance Bar Chart */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Car className="w-4 h-4 text-[#D4AF37]" />
                        Vehicle Performance
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {analytics.charts?.vehicle_bar?.length > 0 ? (
                        <ResponsiveContainer width="100%" height={280}>
                          <BarChart data={analytics.charts.vehicle_bar} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                            <XAxis type="number" tickFormatter={formatCompact} tick={{ fontSize: 11 }} stroke="#94A3B8" />
                            <YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 10 }} stroke="#94A3B8" />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend />
                            <Bar dataKey="revenue" fill="#D4AF37" name="Revenue" radius={[0, 4, 4, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <p className="text-center text-slate-400 py-12">No vehicle data available</p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Driver Performance Bar Chart */}
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Users className="w-4 h-4 text-[#D4AF37]" />
                        Top Drivers by Revenue
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {analytics.charts?.driver_bar?.length > 0 ? (
                        <ResponsiveContainer width="100%" height={280}>
                          <BarChart data={analytics.charts.driver_bar}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                            <XAxis dataKey="name" tick={{ fontSize: 10 }} stroke="#94A3B8" angle={-45} textAnchor="end" height={60} />
                            <YAxis tickFormatter={formatCompact} tick={{ fontSize: 11 }} stroke="#94A3B8" />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend />
                            <Bar dataKey="revenue" fill="#10B981" name="Revenue" radius={[4, 4, 0, 0]} />
                            <Bar dataKey="trips" fill="#3B82F6" name="Trips" radius={[4, 4, 0, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <p className="text-center text-slate-400 py-12">No driver data available</p>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Trip Type Pie Chart */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Trip Types</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {analytics.charts?.trip_type_pie?.length > 0 ? (
                        <ResponsiveContainer width="100%" height={200}>
                          <PieChart>
                            <Pie
                              data={analytics.charts.trip_type_pie}
                              cx="50%"
                              cy="50%"
                              outerRadius={70}
                              dataKey="value"
                              label={({ name, value }) => `${name}: ${value}`}
                            >
                              {analytics.charts.trip_type_pie.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      ) : (
                        <p className="text-center text-slate-400 py-8">No data</p>
                      )}
                    </CardContent>
                  </Card>

                  <Card className="lg:col-span-2">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base">Revenue vs Expenses</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={200}>
                        <BarChart data={analytics.charts?.revenue_vs_expense || []} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                          <XAxis type="number" tickFormatter={formatCompact} tick={{ fontSize: 11 }} />
                          <YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 11 }} />
                          <Tooltip formatter={(value) => formatCurrency(value)} />
                          <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                            {(analytics.charts?.revenue_vs_expense || []).map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.fill || COLORS[index]} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </div>
              </>
            ) : (
              <p className="text-center text-slate-500 py-12">Click Refresh to load analytics</p>
            )}
          </TabsContent>

          {/* REPORTS TAB */}
          <TabsContent value="reports" className="mt-6 space-y-6">
            {/* Monthly P&L Report */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                      <TrendingUp className="w-5 h-5 text-[#D4AF37]" />
                      P&L Report
                    </CardTitle>
                    <CardDescription>
                      {plReport?.period ? `Period: ${plReport.period}` : 'Profit & Loss statement'}
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button onClick={fetchPlReport} variant="outline" size="sm">
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                    <Button onClick={() => downloadPdf('pl')} className="bg-[#D4AF37] hover:bg-[#B5952F] text-black" data-testid="download-pl-pdf-btn">
                      <Download className="w-4 h-4 mr-2" />
                      PDF
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {plReport ? (
                  <div className="space-y-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200">
                        <p className="text-sm text-emerald-600">Total Revenue</p>
                        <p className="text-xl font-bold text-emerald-700 tabular-nums">
                          {formatCurrency(plReport.summary?.total_revenue)}
                        </p>
                      </div>
                      <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                        <p className="text-sm text-blue-600">Total CAPEX</p>
                        <p className="text-xl font-bold text-blue-700 tabular-nums">
                          {formatCurrency(plReport.summary?.total_capex)}
                        </p>
                      </div>
                      <div className="p-4 bg-orange-50 rounded-xl border border-orange-200">
                        <p className="text-sm text-orange-600">Total OPEX</p>
                        <p className="text-xl font-bold text-orange-700 tabular-nums">
                          {formatCurrency(plReport.summary?.total_opex)}
                        </p>
                      </div>
                      <div className={`p-4 rounded-xl border ${plReport.summary?.net_profit >= 0 ? 'bg-[#D4AF37]/10 border-[#D4AF37]/30' : 'bg-red-50 border-red-200'}`}>
                        <p className={`text-sm ${plReport.summary?.net_profit >= 0 ? 'text-[#B5952F]' : 'text-red-600'}`}>Net Profit</p>
                        <p className={`text-xl font-bold tabular-nums ${plReport.summary?.net_profit >= 0 ? 'text-[#D4AF37]' : 'text-red-700'}`}>
                          {formatCurrency(plReport.summary?.net_profit)}
                        </p>
                      </div>
                    </div>

                    {Object.keys(plReport.expense_breakdown || {}).length > 0 && (
                      <div>
                        <h4 className="font-medium mb-3">Expense Breakdown</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          {Object.entries(plReport.expense_breakdown).map(([cat, amount]) => (
                            <div key={cat} className="p-3 bg-slate-50 rounded-lg">
                              <p className="text-xs text-slate-500 truncate">{cat}</p>
                              <p className="font-semibold tabular-nums">{formatCurrency(amount)}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-center text-slate-500 py-8">Click Refresh to load report</p>
                )}
              </CardContent>
            </Card>

            {/* Vehicle Profitability Report */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                      <Car className="w-5 h-5 text-[#D4AF37]" />
                      Vehicle Profitability
                    </CardTitle>
                  </div>
                  <Button onClick={() => downloadPdf('vehicle')} className="bg-[#D4AF37] hover:bg-[#B5952F] text-black" data-testid="download-vehicle-pdf-btn">
                    <Download className="w-4 h-4 mr-2" />
                    PDF
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {vehicleReport?.vehicles?.length > 0 ? (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Vehicle</TableHead>
                          <TableHead>Trips</TableHead>
                          <TableHead>Distance</TableHead>
                          <TableHead>Revenue</TableHead>
                          <TableHead>OPEX</TableHead>
                          <TableHead>Net Profit</TableHead>
                          <TableHead>ROI %</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {vehicleReport.vehicles.map((v) => (
                          <TableRow key={v.vehicle_id}>
                            <TableCell>
                              <div>
                                <p className="font-medium">{v.registration_number}</p>
                                <p className="text-sm text-slate-500">{v.make_model}</p>
                              </div>
                            </TableCell>
                            <TableCell className="tabular-nums">{v.total_trips}</TableCell>
                            <TableCell className="tabular-nums">{v.total_distance_km.toLocaleString()} km</TableCell>
                            <TableCell className="tabular-nums">{formatCurrency(v.total_revenue)}</TableCell>
                            <TableCell className="tabular-nums">{formatCurrency(v.opex)}</TableCell>
                            <TableCell className={`tabular-nums font-medium ${v.net_profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                              {formatCurrency(v.net_profit)}
                            </TableCell>
                            <TableCell className={`tabular-nums ${v.roi_percent >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                              {v.roi_percent}%
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <p className="text-center text-slate-500 py-8">No vehicles found</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* EXPORT TAB */}
          <TabsContent value="export" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                      <Car className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold">Vehicles</h3>
                      <p className="text-sm text-slate-500">Export to Excel</p>
                    </div>
                  </div>
                  <Button onClick={() => downloadExcel('vehicles')} className="w-full" variant="outline" data-testid="export-vehicles-btn">
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    Export
                  </Button>
                </CardContent>
              </Card>
              <Card className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                      <Users className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold">Drivers</h3>
                      <p className="text-sm text-slate-500">Export to Excel</p>
                    </div>
                  </div>
                  <Button onClick={() => downloadExcel('drivers')} className="w-full" variant="outline" data-testid="export-drivers-btn">
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    Export
                  </Button>
                </CardContent>
              </Card>
              <Card className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                      <Receipt className="w-6 h-6 text-orange-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold">Expenses</h3>
                      <p className="text-sm text-slate-500">Export to Excel</p>
                    </div>
                  </div>
                  <Button onClick={() => downloadExcel('expenses')} className="w-full" variant="outline" data-testid="export-expenses-btn">
                    <FileSpreadsheet className="w-4 h-4 mr-2" />
                    Export
                  </Button>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* IMPORT TAB */}
          <TabsContent value="import" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                      <Car className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold">Import Vehicles</h3>
                      <p className="text-sm text-slate-500">Bulk import CSV</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Button onClick={() => handleImport('vehicles')} className="w-full bg-[#D4AF37] hover:bg-[#B5952F] text-black" disabled={importing} data-testid="import-vehicles-btn">
                      {importing && importType === 'vehicles' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                      Upload CSV
                    </Button>
                    <Button onClick={() => downloadTemplate('vehicles')} variant="ghost" size="sm" className="w-full text-slate-500">
                      Download Template
                    </Button>
                  </div>
                </CardContent>
              </Card>
              <Card className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                      <Users className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold">Import Drivers</h3>
                      <p className="text-sm text-slate-500">Bulk import CSV</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Button onClick={() => handleImport('drivers')} className="w-full bg-[#D4AF37] hover:bg-[#B5952F] text-black" disabled={importing} data-testid="import-drivers-btn">
                      {importing && importType === 'drivers' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                      Upload CSV
                    </Button>
                    <Button onClick={() => downloadTemplate('drivers')} variant="ghost" size="sm" className="w-full text-slate-500">
                      Download Template
                    </Button>
                  </div>
                </CardContent>
              </Card>
              <Card className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                      <Receipt className="w-6 h-6 text-orange-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold">Import Expenses</h3>
                      <p className="text-sm text-slate-500">Bulk import CSV</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Button onClick={() => handleImport('expenses')} className="w-full bg-[#D4AF37] hover:bg-[#B5952F] text-black" disabled={importing} data-testid="import-expenses-btn">
                      {importing && importType === 'expenses' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                      Upload CSV
                    </Button>
                    <Button onClick={() => downloadTemplate('expenses')} variant="ghost" size="sm" className="w-full text-slate-500">
                      Download Template
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default Reports;
